import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { translateError } from "@/common/utils/errorHandling";
import { ErrorResponse } from "@reflag/shared/api";
import {
  RotateSDKKeyBody,
  RotateSDKKeyResponse,
} from "@reflag/shared/environmentAPI";

import { environmentQueryKeys } from "./environmentQueryKeys";

export function useSDKKeyRotateMutation() {
  const { appId } = useEnvironment();
  const queryClient = useQueryClient();
  const toast = useToast();
  const errorToast = useErrorToast();

  return useMutation<
    RotateSDKKeyResponse,
    AxiosError<ErrorResponse>,
    { envId: string } & RotateSDKKeyBody
  >({
    mutationFn: ({ envId, keyType }) =>
      api
        .post<"/apps/:appId/environments/:envId/rotate-key">(
          `/apps/${appId}/environments/${envId}/rotate-key`,
          { keyType },
        )
        .then((res) => res.data),
    onSuccess: (_data, { envId, keyType }) => {
      queryClient.invalidateQueries({
        queryKey: environmentQueryKeys.single(appId, envId),
      });

      toast({
        title: `${keyType === "publishable" ? "Publishable" : "Secret"} key rotated`,
        status: "success",
      });
    },
    onError: (error, { keyType }) => {
      const { title, description } = translateError(
        error,
        `Failed to rotate ${keyType} key`,
      );

      errorToast({
        title,
        description,
        duration: 5000,
      });
    },
  });
}
